<template>
    <div>
        <video ref="videoPlayer" class="video-js vjs-show-big-play-button-on-pause"></video>
    </div>
</template>

<script>
import videojs from 'video.js';
import 'video.js/dist/video-js.css'
import { Screen } from '@/utils/Capacitor';
import SafeArea from '@/plugins/safeArea';
import StatusBarManager from '../../utils/StatusBarManager';

export default {
    name: "VideoPlayer",
    props: {
        options: {
            type: Object,
            default() {
                return {};
            }
        }
    },
    data() {
        return {
            player: null
        }
    },
    mounted() {
        this.init()
    },
    methods: {
        init() {
            const vm = this
            this.player = videojs(this.$refs.videoPlayer, {
                preload: 'none',
                ...this.options,
                controlBar: {
                    pictureInPictureToggle: false
                }
            }, function onPlayerReady() {
                this.on('fullscreenchange', () => vm.onFullscreenChange())
            })
            this.setupFullscreenFallback()
        },
        setupFullscreenFallback() {
            const vm = this
            const player = this.player
            const requestFullscreen = player.requestFullscreen.bind(player)
            player.requestFullscreen = options => {
                const video = vm.$refs.videoPlayer
                const nativeReady = !video || video.webkitSupportsFullscreen !== false
                player.options({ preferFullWindow: !nativeReady })
                const promise = requestFullscreen(options)
                if (promise && promise.catch) promise.catch(() => {})
                return promise
            }
        },
        async onFullscreenChange() {
            if (!this.player) return
            if (this.player.isFullscreen()) {
                StatusBarManager.hide()
                const w = this.player.videoWidth()
                const h = this.player.videoHeight()
                if (w && h && h > w) await Screen.lockPortrait()
                else await Screen.lockLandscape()
            } else {
                StatusBarManager.show()
                await Screen.lockPortrait()
            }
            this.refreshSafeArea()
        },
        refreshSafeArea() {
            SafeArea.refresh()
            setTimeout(() => SafeArea.refresh(), 400)
        },
        dispose() {
            Screen.lockPortrait()
            this.refreshSafeArea()
            StatusBarManager.show()
            if (this.player) {
                if (this.player.isFullWindow) this.player.exitFullWindow()
                this.player.dispose()
            }
        },
    },
    beforeDestroy() {
        this.dispose()
    },
}
</script>

<style scoped>
.video-js {
    background-color: #000 !important;
    width: 100%;
    aspect-ratio: 16 / 9;
    max-height: 50vh;
    border-radius: 8px;
    overflow: hidden;
}

::v-deep .vjs-tech,
::v-deep .vjs-poster {
    position: absolute;
    top: 0;
    left: 0;
    width: 100% !important;
    height: 100% !important;
    max-width: none !important;
    max-height: none !important;
    padding-top: 0 !important;
    object-fit: contain;
    border-radius: 8px;
}

::v-deep .video-js .vjs-control-bar {
    border-radius: 0 0 8px 8px;
    margin-bottom: 0;
    width: 100%;
    height: auto;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    color: #F2F5FA;
    background: rgba(6, 8, 12, 0.6);
    backdrop-filter: blur(12px);
}

::v-deep .video-js .vjs-control-bar .vjs-control {
    height: 40px;
}

::v-deep .video-js .vjs-control-bar .vjs-button > .vjs-icon-placeholder:before {
    line-height: 40px;
}

::v-deep .video-js .vjs-time-control {
    display: flex;
    align-items: center;
    height: 40px;
    padding: 0;
    min-width: 0;
    font-size: 12px;
}

::v-deep .video-js .vjs-current-time {
    display: flex !important;
    padding-left: 2px;
}

::v-deep .video-js .vjs-time-divider {
    display: flex !important;
    min-width: 0;
    padding: 0 2px;
}

::v-deep .video-js .vjs-duration {
    display: flex !important;
}

::v-deep .video-js .vjs-remaining-time {
    display: none !important;
}

::v-deep .video-js .vjs-fullscreen-control {
    margin-left: auto;
}

::v-deep .video-js .vjs-control-bar .vjs-progress-control {
    order: -1;
    flex: 0 0 100%;
    width: 100%;
    min-width: 0;
    height: 0;
    margin: 0;
    padding: 0;
    display: flex;
    align-items: center;
}

::v-deep .video-js .vjs-control-bar .vjs-progress-control .vjs-progress-holder {
    position: relative;
    margin: 0;
    height: 5px;
    width: 100%;
    background: rgba(242, 245, 250, 0.5);
}

::v-deep .vjs-time-tooltip,
::v-deep .vjs-volume-tooltip {
    background: var(--v-bg-base);
    color: var(--v-txtPrimary-base);
    opacity: .85;
}

::v-deep .vjs-load-progress {
    background: rgba(242, 245, 250, 0.5);
}

::v-deep .vjs-load-progress div {
    background: transparent;
}

::v-deep .vjs-play-progress {
    background: var(--brand-gradient);
}

::v-deep .vjs-mouse-display:before {
    content: '';
    display: block;
    position: absolute;
    right: 0;
    top: 50%;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #F2F5FA;
    box-shadow: 0 0 0 3px rgba(204, 255, 51, 0.35);
    transform: translate(50%, -50%);
    transition: box-shadow 0.12s ease;
}

::v-deep .vjs-play-progress:before {
    content: '';
    display: block;
    position: absolute;
    right: 0;
    top: 50%;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #F2F5FA;
    box-shadow: 0 0 0 3px rgba(204, 255, 51, 0.35);
    transform: translate(50%, -50%);
    transition: box-shadow 0.12s ease;
}

::v-deep .vjs-progress-control:hover .vjs-play-progress:before {
    box-shadow: 0 0 0 5px rgba(204, 255, 51, 0.45);
}

::v-deep .vjs-poster {
    border-radius: 8px;
}

::v-deep .video-js .vjs-big-play-button {
    height: 72px;
    width: 72px;
    background: rgba(8, 10, 14, 0.5);
    backdrop-filter: blur(8px);
    border: 1.5px solid rgba(204, 255, 51, 0.45);
    border-radius: 50%;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    transition: background 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
}

::v-deep .video-js .vjs-big-play-button:hover,
::v-deep .video-js:hover .vjs-big-play-button {
    background: rgba(8, 10, 14, 0.7);
    border-color: rgba(204, 255, 51, 0.85);
    transform: translate(-50%, -50%) scale(1.06);
}

::v-deep .video-js .vjs-big-play-button .vjs-icon-placeholder:before {
    content: "";
    background-image: url('~@/assets/img/play_white.png');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    width: 35px;
    height: 35px;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
}

/* Volumen: slider horizontal centrado verticalmente al hover */
::v-deep .video-js .vjs-volume-panel {
    display: flex;
    align-items: center;
    height: 40px;
}

::v-deep .video-js .vjs-volume-control.vjs-volume-horizontal {
    display: flex;
    align-items: center;
    height: 40px;
}

::v-deep .video-js .vjs-volume-bar.vjs-slider-horizontal {
    height: 5px;
    margin-top: 0;
    margin-bottom: 0;
    border-radius: 3px;
}

::v-deep .vjs-volume-level {
    background: var(--v-primary-base);
    border-radius: 3px;
}

::v-deep .vjs-volume-bar.vjs-slider-horizontal .vjs-volume-level:before {
    content: '';
    display: block;
    position: absolute;
    right: 0;
    top: 50%;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #F2F5FA;
    box-shadow: 0 0 0 3px rgba(204, 255, 51, 0.35);
    transform: translate(50%, -50%);
    transition: box-shadow 0.12s ease;
}

::v-deep .vjs-slider-vertical .vjs-volume-level:before {
    color: var(--v-primary-base);
}

::v-deep .video-js .vjs-control:focus:before,
::v-deep .video-js .vjs-control:hover:before {
    text-shadow: 0 0 8px rgba(204, 255, 51, 0.6);
}

/* FULLSCREEN CONTAINER */
::v-deep .video-js.vjs-fullscreen {
    display: flex !important;
    align-items: center;
    justify-content: center;

    width: 100% !important;
    height: 100% !important;
    max-height: none !important;

    padding-top: var(--safe-area-inset-top, 0px);
    padding-bottom: var(--safe-area-inset-bottom, 0px);
    padding-left: var(--safe-area-inset-left, 0px);
    padding-right: var(--safe-area-inset-right, 0px);

    box-sizing: border-box;
    border-radius: 0 !important;
    background: black;
}

/* VIDEO FULLSCREEN */
::v-deep .video-js.vjs-fullscreen .vjs-tech,
::v-deep .video-js.vjs-fullscreen video {
    width: 100% !important;
    height: 100% !important;
    max-height: none !important;
    max-width: none !important;

    object-fit: contain;
    border-radius: 0 !important;
}

/* CONTROL BAR FULLSCREEN */
::v-deep .video-js.vjs-fullscreen .vjs-control-bar {
    margin-bottom: var(--safe-area-inset-bottom, 0px);

    left: var(--safe-area-inset-left, 0px);
    right: var(--safe-area-inset-right, 0px);

    width: auto;
    border-radius: 0;
}
</style>